import { ChangeEvent, FC, useLayoutEffect, useMemo, useReducer, useState } from 'react';
import { Field, Form, Label } from '../../Registration.styles';
import { Dropdown, ErrorScreen, Input, Loading, Message } from '../../../../components';
import { Option } from '../../../../components/dropdown/Dropdown';
import { RegistrationFormPage } from '../../templates';
import { opcoesDeDocumento, opcoesDeSimOuNao, opcoesDeSimOuNaoPendente } from '../../../../utils';
import {
	newShopkeeperFormReducer,
	newShopkeeperFormInitialState,
	tranformShopkeeperResponseIntoForm,
	transformStateIntoShopkeeperRequest,
} from './newShopkeeper.form';
import { useToastStore } from '../../../../stores';
import { useNavigate, useParams } from 'react-router-dom';
import { getBuyerById, postBuyer, putBuyer } from '../../../../services';

const NewShopkeeper: FC = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { setToast } = useToastStore();
	const [state, dispatch] = useReducer(newShopkeeperFormReducer, newShopkeeperFormInitialState);
	const documentoSelecionado = useMemo(
		() => opcoesDeDocumento.find((document) => document.value === state.tipoDocumento),
		[state.tipoDocumento]
	);
	const statusAtivoSelecionado = useMemo(
		() => opcoesDeSimOuNao.find((status) => status.value === state.tagAtivo),
		[state.tagAtivo]
	);

	const statusDeferidoSelecionado = useMemo(
		() => opcoesDeSimOuNaoPendente.find((status) => status.value === state.tagDeferimento),
		[state.tagDeferimento]
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		dispatch({ type: 'SET_VALUE', field: name, payload: value });
	};

	const handleSelectOption = (field: string, option: Option) => {
		dispatch({ type: 'SET_SELECT_OPTION', field: field, value: option.value });
	};

	const handleCreate = async () => {
		const requestBody = transformStateIntoShopkeeperRequest(state);

		try {
			const response = await postBuyer(requestBody);

			if (!response) {
				throw new Error();
			}

			setToast({ message: 'Lojista criado com sucesso!', variant: 'success' });

			navigate(`/cadastros/lojistas`);
		} catch {
			setToast({ message: 'Erro ao criar lojista. Tente novamente.', variant: 'error' });
		}
	};

	const handleEdit = async () => {
		const requestBody = transformStateIntoShopkeeperRequest(state);

		try {
			await putBuyer(id!, requestBody);
			setToast({ message: 'Lojista atualizado com sucesso!', variant: 'success' });
			navigate(`/cadastros/lojistas`);
		} catch {
			setToast({ message: 'Erro ao atualizar lojista. Tente novamente.', variant: 'error' });
		}
	};

	const handleSubmit = () => {
		// if (!isFormValid(state)) {
		// setToast({ message: 'Preencha todos os campos', variant: 'error' });
		// return;
		// }

		if (!!id) {
			handleEdit();
		} else {
			handleCreate();
		}
	};

	useLayoutEffect(() => {
		const fetchBuyer = async () => {
			if (!id) return;

			setLoading(true);

			try {
				const response = await getBuyerById({ id });

				if (!response) {
					throw new Error();
				}

				const formInitialState = tranformShopkeeperResponseIntoForm(response);

				dispatch({ type: 'REPLACE_STATE', payload: formInitialState });
			} catch {
				setToast({ message: 'Erro ao buscar lojista. Tente novamente.', variant: 'error' });
				setError(true);
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		fetchBuyer();
	}, [id, setToast]);

	if (error) return <ErrorScreen fullScreen />;

	if (loading) return <Loading fullScreen />;

	return (
		<RegistrationFormPage title='Cadastrar Lojista' onSubmit={handleSubmit}>
			<Form>
				<Label>Razão Social</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Razão Social'
						value={state.razaoSocial}
						onChange={handleInputChange}
						name='razaoSocial'
					/>
				</Field>
				<Label>Nome Fantasia</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Nome Fantasia'
						value={state.fantasia}
						onChange={handleInputChange}
						name='fantasia'
					/>
				</Field>
				<Label>Tipo Documento</Label>
				<Field>
					<Dropdown
						options={opcoesDeDocumento}
						selected={documentoSelecionado}
						onSelect={(option: Option) => handleSelectOption('tipoDocumento', option)}
					/>
				</Field>
				<Label>Número do Documento</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Número do Documento'
						value={state.numeroDocumento}
						onChange={handleInputChange}
						name='numeroDocumento'
						maxLength={documentoSelecionado?.label === 'CPF' ? 14 : 20}
					/>
				</Field>
				<Label>Inscrição Estadual</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Inscrição Estadual'
						value={state.inscricaoEstadual}
						onChange={handleInputChange}
						name='inscricaoEstadual'
					/>
				</Field>
				<Label>Inscrição Municipal</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Inscrição Municipal'
						value={state.inscricaoMunicipal}
						onChange={handleInputChange}
						name='inscricaoMunicipal'
					/>
				</Field>
				<Label>E-mail</Label>
				<Field>
					<Input
						variant='text'
						placeholder='E-mail'
						value={state.email}
						onChange={handleInputChange}
						name='email'
					/>
				</Field>
				<Label>Telefone</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Telefone'
						value={state.telefone}
						onChange={handleInputChange}
						name='telefone'
						maxLength={14}
					/>
				</Field>
				<Label>Celular</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Celular'
						value={state.celular}
						onChange={handleInputChange}
						name='celular'
						maxLength={15}
					/>
				</Field>
				<Label>CEP</Label>
				<Field>
					<Input
						variant='text'
						placeholder='CEP'
						value={state.cep}
						onChange={handleInputChange}
						name='cep'
						maxLength={9}
					/>
				</Field>
				<Label>Logradouro</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Logradouro'
						value={state.logradouro}
						onChange={handleInputChange}
						name='logradouro'
					/>
				</Field>
				<Label>Número</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Número'
						value={state.numeroLogradouro}
						onChange={handleInputChange}
						name='numeroLogradouro'
					/>
				</Field>
				<Label>Complemento</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Complemento'
						value={state.complementoLogradouro}
						onChange={handleInputChange}
						name='complementoLogradouro'
					/>
				</Field>
				<Label>Bairro</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Bairro'
						value={state.bairro}
						onChange={handleInputChange}
						name='bairro'
					/>
				</Field>
				<Label>Cidade</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Cidade'
						value={state.cidade}
						onChange={handleInputChange}
						name='cidade'
					/>
				</Field>
				<Label>Estado</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Estado'
						value={state.estado}
						onChange={handleInputChange}
						name='estado'
					/>
				</Field>
				<Label>Responsável</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Responsável'
						value={state.responsavel}
						onChange={handleInputChange}
						name='responsavel'
					/>
				</Field>
				<Label>Indicação</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Indicação'
						value={state.indicacao || ''}
						onChange={handleInputChange}
						name='indicacao'
					/>
				</Field>
				<Label>Ativo?</Label>
				<Field>
					<Dropdown
						options={opcoesDeSimOuNao}
						selected={statusAtivoSelecionado}
						onSelect={(option: Option) => handleSelectOption('tagAtivo', option)}
					/>
				</Field>
				<Label>Deferido?</Label>
				<Field>
					<Dropdown
						options={opcoesDeSimOuNaoPendente}
						selected={statusDeferidoSelecionado}
						onSelect={(option: Option) => handleSelectOption('tagDeferimento', option)}
					/>
				</Field>
				<Label>Observação</Label>
				<Field>
					<Message
						placeholder='Observação'
						value={state.observacao || ''}
						onChange={handleInputChange}
						name='observacao'
					/>
				</Field>
			</Form>
		</RegistrationFormPage>
	);
};

export default NewShopkeeper;
